import * as React from "react";
import { createClasses } from "@bloomberg/media-ui-core";

import { SUPPORTED_THEMES } from "~constants";
import ItemsWithHeadshots from "~components/shared/items-with-headshots/ItemsWithHeadshots";
import {
    type MediaConfig,
    Media,
    MediaFormat,
    getWinningMediaFormat,
} from "~components/shared/media";
import { StoryBlock } from "~components/shared/story-block/StoryBlock";
import TickerPills from "~components/shared/ticker-pills/TickerPills";
import { VariantBaseWithHeader } from "~components/shared/variant-base/VariantBase";
import {
    type VariationComponent,
    type CuratedItem,
    ZoneColumnCombo,
} from "~types/types";
import { integrityError } from "~utils/moduleIntegrity";

import integrityCheck from "./LineupContentOpinion2Up.integrity";
import { PREVIEW_NOTES } from "./previewNotes";
import styles from "./LineupContentOpinion2Up.module.css";

const SUPPORTED_REGIONS: ZoneColumnCombo[] = [ZoneColumnCombo.leftContentWell];

export const MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.snapshotChart,
    MediaFormat.embeddedIframe,
    MediaFormat.loopingVideo,
    MediaFormat.vodVideo,
    MediaFormat.livestreamVideo,
    MediaFormat.defaultImage,
];

const PRIMARY_ITEM_MEDIA_PRIORITY: MediaFormat[] = [
    MediaFormat.verticalVideo,
    ...MEDIA_PRIORITY,
];

const MEDIA_CONFIG: MediaConfig = {
    desktopMaxWidth: 350,
    mobileMaxWidth: 715,
    aspectRatio: 3 / 2,
    className: styles.media,
    showCaption: false,
};

const PRIMARY_ITEM_MEDIA_CONFIG: MediaConfig = {
    ...MEDIA_CONFIG,
    desktopMaxWidth: 459,
};

const PrimaryItem = ({ item }: { item: CuratedItem }) => {
    const isVerticalVideo =
        getWinningMediaFormat(item, PRIMARY_ITEM_MEDIA_PRIORITY) ===
        MediaFormat.verticalVideo;

    // show if not vertical video story type or if vertical video decoration override
    const showStoryBlock = !isVerticalVideo || item.videoCurationOverride;

    return (
        <div className={createClasses([styles.item, styles.primaryItem])}>
            <Media
                source={item}
                priority={PRIMARY_ITEM_MEDIA_PRIORITY}
                config={PRIMARY_ITEM_MEDIA_CONFIG}
            />
            <div className={styles.content}>
                {showStoryBlock && (
                    <StoryBlock
                        story={item}
                        isBylineEligible
                        showVerticalVideoHeadline={isVerticalVideo}
                        storyIndex={1}
                        withDek={Boolean(item.summary) && !isVerticalVideo}
                        withOptionalEyebrow={false}
                    />
                )}
                <TickerPills storyIndex={1} tickerIds={item.terminalTickers} />
            </div>
        </div>
    );
};

const SecondaryItem = ({ item }: { item: CuratedItem }) => {
    return (
        <div className={createClasses([styles.item, styles.secondaryItem])}>
            <Media
                source={item}
                priority={MEDIA_PRIORITY}
                config={MEDIA_CONFIG}
            />
            <div className={styles.content}>
                <StoryBlock
                    story={item}
                    isBylineEligible
                    storyIndex={2}
                    withDek={Boolean(item.summary)}
                    withOptionalEyebrow={false}
                />
                <TickerPills storyIndex={2} tickerIds={item.terminalTickers} />
            </div>
        </div>
    );
};

export default function LineupContentOpinion2Up(props: VariationComponent) {
    const { items = [], theme } = props;

    const integrity = integrityCheck({ ...props });
    if (integrity.length) return integrityError(integrity);
    if (!items?.length || items.length < 2) return null;

    // Odd pattern, the existence of module.theme = opinion
    // means this module is being used on an Opinion section
    // front. In that case, we do not want to render an
    // opinion-blue backgound. When the module is used in
    // any other context, it should have the opinion-blue
    // background.
    const gridClasses = createClasses([
        styles.grid,
        {
            [styles.useOpinionBackground]:
                theme !== SUPPORTED_THEMES.opinionPage,
        },
    ]);
    const [primaryItem, secondaryItem, ...headshotItems] = items;

    return (
        <VariantBaseWithHeader
            source={props}
            className={styles.LineupContentOpinion2Up}
            gridClassName={gridClasses}
            supportedRegions={SUPPORTED_REGIONS}
            previewNotes={PREVIEW_NOTES}
        >
            <div className={styles.twoUp}>
                <PrimaryItem item={primaryItem} />
                <SecondaryItem item={secondaryItem} />
            </div>
            {Boolean(headshotItems?.length) && (
                <div className={styles.headshots}>
                    <ItemsWithHeadshots items={headshotItems} isOpinion />
                </div>
            )}
        </VariantBaseWithHeader>
    );
}
